import React from 'react';
import { Link } from 'react-router-dom';
import { useMessageNotifications } from '../../contexts/MessageNotificationContext';

const DROPDOWN_ID = 'messagesDropdown';
const MAX_ITEMS = 20;

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'şimdi';
  if (diffMin < 60) return `${diffMin} dk`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} sa`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} gün`;
  return new Date(ts).toLocaleDateString('tr-TR');
}

interface MessageCenterDropdownProps {
  /** Tighter spacing when placed beside notification bell. */
  compact?: boolean;
}

const MessageCenterDropdown: React.FC<MessageCenterDropdownProps> = ({ compact = false }) => {
  const { messageUnreadCount, conversationSummaries, markConversationRead } =
    useMessageNotifications();

  const items = conversationSummaries.slice(0, MAX_ITEMS);

  return (
    <div className={`dropdown position-relative${compact ? '' : ' me-2'}`}>
      <button
        type="button"
        className="btn btn-link text-primary dropdown-toggle d-flex align-items-center border-0 position-relative p-2"
        style={{ textDecoration: 'none', fontWeight: 'normal' }}
        id={DROPDOWN_ID}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={messageUnreadCount > 0 ? 'Mesajlar (yeni)' : 'Mesajlar'}
      >
        <i className="fas fa-envelope fs-5" />
        {messageUnreadCount > 0 && (
          <span
            className="sb-header-unread-dot position-absolute top-0 end-0 translate-middle rounded-circle bg-danger"
            style={{ width: '0.5rem', height: '0.5rem' }}
            aria-hidden="true"
          />
        )}
      </button>
      <ul
        className="dropdown-menu dropdown-menu-end p-0"
        aria-labelledby={DROPDOWN_ID}
        style={{ borderTop: '3px solid #0d6efd', minWidth: '320px', maxWidth: '380px' }}
      >
        <li>
          <h6 className="dropdown-header bg-primary text-white m-0 px-3 py-2">Mesajlar</h6>
        </li>
        {items.length === 0 ? (
          <li>
            <div className="px-3 py-3 text-muted small text-center">Henüz mesaj yok.</div>
          </li>
        ) : (
          items.map((s) => (
            <li key={s.conversationId}>
              <Link
                to={`/mesajlar?conversationId=${s.conversationId}`}
                className="dropdown-item px-3 py-2 d-flex flex-column"
                onClick={() => {
                  void markConversationRead(s.conversationId);
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="fw-semibold text-truncate"
                    style={{ maxWidth: '220px' }}
                    title={s.peerDisplayName}
                  >
                    {s.peerDisplayName}
                  </span>
                  <small className="text-muted ms-2">{formatRelativeTime(s.lastMessageAt)}</small>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small
                    className="text-muted text-truncate"
                    style={{ maxWidth: '260px' }}
                    title={s.lastMessagePreview ?? ''}
                  >
                    {s.lastMessagePreview ?? ''}
                  </small>
                  {s.unreadMessageCount > 0 && (
                    <span className="sb-header-unread-dot rounded-circle bg-danger ms-2 flex-shrink-0" style={{ width: '0.5rem', height: '0.5rem', display: 'inline-block' }} aria-hidden="true" />
                  )}
                </div>
              </Link>
            </li>
          ))
        )}
        <li>
          <div className="dropdown-divider m-0" />
        </li>
        <li>
          <Link to="/mesajlar" className="dropdown-item text-center text-primary fw-semibold py-2">
            Tüm Mesajları Görüntüle
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default MessageCenterDropdown;
